import { useEffect, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Eye, EyeOff, Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Login() {
  const { login } = useAuth();
  const [nomorPegawai, setNomorPegawai] = useState(localStorage.getItem("remember_nomor") || "");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState(localStorage.getItem("login_area") || "");
  useEffect(() => {
    api.get("/public/areas").then((r) => {
      setAreas(r.data);
      setArea((cur) => (r.data.some((a) => a.kode === cur) ? cur : (r.data.find((a) => a.is_default)?.kode || r.data[0]?.kode || "")));
    }).catch(() => setAreas([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      localStorage.setItem("login_area", area);
      await login(nomorPegawai, password, remember, area);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-slate-50">
      <div className="w-full max-w-sm">
        <div className="flex items-center mb-8">
          <img src="/logo-patra.png" alt="PATRA Facility Management" className="h-12 w-auto" data-testid="login-logo" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2 text-slate-900">SELAMAT DATANG DI FMS MOR JBB</h2>
        <p className="text-sm text-slate-500 mt-2 mb-8">Masukkan kredensial admin untuk melanjutkan.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            {areas.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="login-area" className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-slate-500" />Area</Label>
                <Select value={area} onValueChange={setArea}>
                  <SelectTrigger id="login-area" className="h-11" data-testid="login-area-select"><SelectValue placeholder="Pilih area" /></SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => <SelectItem key={a.kode} value={a.kode} data-testid={`login-area-${a.kode}`}>{a.nama}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="nomor-pegawai">Nomor Pekerja</Label>
              <Input id="nomor-pegawai" type="text" required value={nomorPegawai}
                onChange={(e) => setNomorPegawai(e.target.value)}
                placeholder="59140756" data-testid="login-nomor-input" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Kata Sandi</Label>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  data-testid="login-password-input" className="h-11 pr-11" />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="toggle-password-visibility">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="remember-me" checked={remember}
                onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember-checkbox" />
              <Label htmlFor="remember-me" className="text-sm font-normal text-slate-600 cursor-pointer">
                Ingat saya di perangkat ini
              </Label>
            </div>
            {error && <p className="text-sm text-rose-600 font-medium" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition-colors">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
            </Button>
          </form>
      </div>
    </div>
  );
}
